import { registerUIDeserializable } from "common/components/deserialization";
import {
    AdaptiveListItemUI,
    AudioPlayerUI,
    AvatarOverlayUI,
    BarChartUI,
    ButtonBarUI,
    ButtonUI,
    BuyScreenUI,
    CalendarListUI,
    CardItemUI,
    ChatUI,
    CheckboxAccessoryUI,
    CheckboxUI,
    CheckListUI,
    ChoiceUI,
    CircleButtonAccessoryUI,
    CircleProgressUI,
    ClickableUI,
    CodeScannerUI,
    ColumnItemUI,
    ColumnListUI,
    ColumnUI,
    CommentsUI,
    DateFieldUI,
    DateTimeFieldUI,
    EmailAuthenticationUI,
    EmailFieldUI,
    EventPickerButtonUI,
    EventPickerUI,
    FilePickerUI,
    GalleryItemUI,
    GalleryListUI,
    GridItemUI,
    GridUI,
    HighlightComponentUI,
    HintUI,
    HoldContainerUI,
    HorizontalTilesItemUI,
    HorizontalTilesUI,
    ImageAccessoryUI,
    ImageItemUI,
    ImagePickerUI,
    InlineListUI,
    LabelUI,
    LikeUI,
    ListItemUI,
    ListUI,
    LiveLinearBarcodeScannerUI,
    LocalTimeUI,
    LocationEntryUI,
    MapListUI,
    MapUI,
    MarkdownItemUI,
    NoteUI,
    NumberFieldUI,
    OverlayEditorUI,
    PhoneFieldUI,
    PhoneNumberUI,
    PieChartUI,
    PivotPickerUI,
    ProgressUI,
    RatingUI,
    ReactionUI,
    SearchBarUI,
    SeparatorUI,
    ShareScreenUI,
    SignatureFieldUI,
    SignaturePadUI,
    SimpleTableUI,
    SmallListUI,
    SpansUI,
    StackedBarChartUI,
    StopwatchUI,
    SwitchUI,
    TagOverlayUI,
    TextBoxUI,
    TextFieldUI,
    TinderUI,
    TitleUI,
    ToggleIconOverlayUI,
    UserProfileUI,
    VerticalTilesItemUI,
    VerticalTilesUI,
    VideoUI,
    WeatherUI,
    WebviewUI,
    YouTubeUI,
} from "common/components/uis";
import { components as fluentComponents } from "fluent-components/fluent-components";
import type { FluentComponentName } from "fluent-components/fluent-components-types";
import { makeFluentComponentUI } from "generator/components/fluent-components-handlers";
import { isStorybook } from "routes";
import { CodeScannerMaterialRenderer } from "webapp/renderers/code-scanner-material-renderer";
import { DateTimeFieldMaterialRenderer } from "webapp/renderers/date-time-material-renderer";
import { EventPickerButtonMaterialRenderer } from "webapp/renderers/event-picker-button-material-renderer";
import { EventPickerMaterialRenderer } from "webapp/renderers/event-picker-material-renderer";
import { makeFluentRenderer } from "webapp/renderers/fluent-renderers";
import { LiveLinearBarcodeScannerMaterialRenderer } from "webapp/renderers/live-linear-barcode-scanner-material-renderer";
import { LocalTimeMaterialRenderer } from "webapp/renderers/local-time-material-renderer";
import { ReactionMaterialRenderer } from "webapp/renderers/reaction-material-renderer";
import { ShareScreenMaterialRenderer } from "webapp/renderers/share-screen-material-renderer";
import { SpansMaterialRenderer } from "webapp/renderers/spans-material-renderer";
import { StackedBarChartMaterialRenderer } from "webapp/renderers/stacked-bar-chart-material-renderer";
import { StopwatchMaterialRenderer } from "webapp/renderers/stopwatch-material-renderer";
import { TinderMaterialRenderer } from "webapp/renderers/tinder-material-renderer";
import { WeatherMaterialRenderer } from "webapp/renderers/weather-material-renderer";
import { AdaptiveListItemMaterialRenderer } from "../renderers/adaptive-list-item-material-renderer";
import { AudioPlayerMaterialRenderer } from "../renderers/audio-player-material-renderer";
import { AvatarOverlayMaterialRenderer } from "../renderers/avatar-overlay-material-renderer";
import { BarChartMaterialRenderer } from "../renderers/bar-chart-material-renderer";
import { ButtonBarMaterialRenderer } from "../renderers/button-bar-material-renderer";
import { ButtonMaterialRenderer } from "../renderers/button-material-renderer";
import { BuyScreenMaterialRenderer } from "../renderers/buy-screen-material-renderer";
import { CalendarListMaterialRenderer } from "../renderers/calendar-list-material-renderer";
import { CardItemMaterialRenderer } from "../renderers/card-item-material-renderer";
import { ChatMaterialRenderer } from "../renderers/chat-material-renderer";
import { CheckboxAccessoryMaterialRenderer } from "../renderers/checkbox-accessory-material-renderer";
import { CheckboxMaterialRenderer } from "../renderers/checkbox-material-renderer";
import { ChoiceMaterialRenderer } from "../renderers/choice-material-renderer";
import { CircleButtonAccessoryMaterialRenderer } from "../renderers/circle-button-accessory-material-renderer";
import { ClickableMaterialRenderer } from "../renderers/clickable-material-renderer";
import { ColumnMaterialRenderer } from "../renderers/column-material-renderer";
import { CommentsMaterialRenderer } from "../renderers/comments-material-renderer";
import { DateFieldMaterialRenderer } from "../renderers/date-field-material-renderer";
import { ColumnItemMaterialRenderer } from "../renderers/deprecated/column-item-material-renderer";
import { ColumnListMaterialRenderer } from "../renderers/deprecated/column-list-material-renderer";
import { GalleryItemMaterialRenderer } from "../renderers/deprecated/gallery-item-material-renderer";
import { GalleryListMaterialRenderer } from "../renderers/deprecated/gallery-list-material-renderer";
import { GridItemMaterialRenderer } from "../renderers/deprecated/grid-item-material-renderer";
import { GridMaterialRenderer } from "../renderers/deprecated/grid-material-renderer";
import { ListItemMaterialRenderer } from "../renderers/deprecated/list-item-material-renderer";
import { YouTubeMaterialRenderer } from "../renderers/deprecated/youtube-material-renderer";
import { EmailAuthenticationMaterialRenderer } from "../renderers/email-authentication-material-renderer";
import { EmailFieldMaterialRenderer } from "../renderers/email-field-material-renderer";
import { FilePickerMaterialRenderer } from "../renderers/file-picker-material-renderer";
import { HighlightComponentMaterialRenderer } from "../renderers/highlight-component-material-renderer";
import { HintMaterialRenderer } from "../renderers/hint-material-renderer";
import { HoldContainerMaterialRenderer } from "../renderers/hold-container-material-renderer";
import { HorizontalTilesItemMaterialRenderer } from "../renderers/horizontal-tiles-item-material-renderer";
import { HorizontalTilesMaterialRenderer } from "../renderers/horizontal-tiles-material-renderer";
import { ImageAccessoryMaterialRenderer } from "../renderers/image-accessory-material-renderer";
import { ImageMaterialRenderer } from "../renderers/image-material-renderer";
import { ImagePickerMaterialRenderer } from "../renderers/image-picker-material-renderer";
import { InlineListMaterialRenderer } from "../renderers/inline-list-material-renderer";
import { LabelMaterialRenderer } from "../renderers/label-material-renderer";
import { LikeMaterialRenderer } from "../renderers/like-material-renderer";
import { ListMaterialRenderer } from "../renderers/list-material-renderer";
import { LocationEntryMaterialRenderer } from "../renderers/location-entry-material-renderer";
import { MapListMaterialRenderer } from "../renderers/map-list-material-renderer";
import { MapMaterialRenderer } from "../renderers/map-material-renderer";
import { MarkdownItemMaterialRenderer } from "../renderers/markdown-item-material-renderer";
import { NoteMaterialRenderer } from "../renderers/note-material-renderer";
import { NumberFieldMaterialRenderer } from "../renderers/number-field-material-renderer";
import { OverlayEditorMaterialRenderer } from "../renderers/overlay-editor-material-renderer";
import { PhoneFieldMaterialRenderer } from "../renderers/phone-field-material-renderer";
import { PhoneNumberMaterialRenderer } from "../renderers/phone-number-material-renderer";
import { PieChartMaterialRenderer } from "../renderers/pie-chart-material-renderer";
import { PivotPickerMaterialRenderer } from "../renderers/pivot-picker-material-renderer";
import { ProgressMaterialRenderer } from "../renderers/progress-material-renderer";
import { RatingMaterialRenderer } from "../renderers/rating-material-renderer";
import { SearchBarMaterialRenderer } from "../renderers/search-bar-material-renderer";
import { SeparatorMaterialRenderer } from "../renderers/separator-material-renderer";
import { SignatureFieldMaterialRenderer } from "../renderers/signature-field-material-renderer";
import { SignaturePadMaterialRenderer } from "../renderers/signature-pad-material-renderer";
import { SimpleTableMaterialRenderer } from "../renderers/simple-table-material-renderer";
import { SwitchMaterialRenderer } from "../renderers/switch-material-renderer";
import { TagOverlayMaterialRenderer } from "../renderers/tag-overlay-material-renderer";
import { TextBoxFunctionalRenderer } from "../renderers/text-box-material-renderer";
import { TextFieldMaterialRenderer } from "../renderers/text-field-material-renderer";
import { TitleMaterialRenderer } from "../renderers/title-material-renderer";
import { ToggleIconOverlayMaterialRenderer } from "../renderers/toggle-icon-overlay-material-renderer";
import { UserProfileMaterialRenderer } from "../renderers/user-profile-material-renderer";
import { VerticalTilesItemMaterialRenderer } from "../renderers/vertical-tiles-item-material-renderer";
import { VerticalTilesMaterialRenderer } from "../renderers/vertical-tiles-material-renderer";
import { VideoMaterialRenderer } from "../renderers/video-material-renderer";
import { WebviewMaterialRenderer } from "../renderers/webview-material-renderer";

export function registerUIs(): void {
    if (isStorybook()) return;
    registerUIDeserializable(LabelUI, LabelMaterialRenderer);
    registerUIDeserializable(CheckboxAccessoryUI, CheckboxAccessoryMaterialRenderer);
    registerUIDeserializable(CircleButtonAccessoryUI, CircleButtonAccessoryMaterialRenderer);
    registerUIDeserializable(ImageAccessoryUI, ImageAccessoryMaterialRenderer);
    registerUIDeserializable(HoldContainerUI, HoldContainerMaterialRenderer);
    registerUIDeserializable(ColumnUI, ColumnMaterialRenderer);
    registerUIDeserializable(SpansUI, SpansMaterialRenderer);
    registerUIDeserializable(ClickableUI, ClickableMaterialRenderer);
    registerUIDeserializable(HighlightComponentUI, HighlightComponentMaterialRenderer);
    registerUIDeserializable(ImageItemUI, ImageMaterialRenderer);
    registerUIDeserializable(TextBoxUI, TextBoxFunctionalRenderer);
    registerUIDeserializable(AudioPlayerUI, AudioPlayerMaterialRenderer);
    registerUIDeserializable(NoteUI, NoteMaterialRenderer);
    registerUIDeserializable(HintUI, HintMaterialRenderer);
    registerUIDeserializable(ShareScreenUI, ShareScreenMaterialRenderer);
    registerUIDeserializable(SearchBarUI, SearchBarMaterialRenderer);
    registerUIDeserializable(SwitchUI, SwitchMaterialRenderer);
    registerUIDeserializable(CheckboxUI, CheckboxMaterialRenderer);
    registerUIDeserializable(ChoiceUI, ChoiceMaterialRenderer);
    registerUIDeserializable(MarkdownItemUI, MarkdownItemMaterialRenderer);
    registerUIDeserializable(ListUI, ListMaterialRenderer);
    registerUIDeserializable(SmallListUI, ListMaterialRenderer);
    registerUIDeserializable(CheckListUI, ListMaterialRenderer);
    registerUIDeserializable(ListItemUI, ListItemMaterialRenderer);
    registerUIDeserializable(AdaptiveListItemUI, AdaptiveListItemMaterialRenderer);
    registerUIDeserializable(MapListUI, MapListMaterialRenderer);
    registerUIDeserializable(PhoneNumberUI, PhoneNumberMaterialRenderer);
    registerUIDeserializable(GridUI, GridMaterialRenderer);
    registerUIDeserializable(GridItemUI, GridItemMaterialRenderer);
    registerUIDeserializable(HorizontalTilesUI, HorizontalTilesMaterialRenderer);
    registerUIDeserializable(HorizontalTilesItemUI, HorizontalTilesItemMaterialRenderer);
    registerUIDeserializable(VerticalTilesUI, VerticalTilesMaterialRenderer);
    registerUIDeserializable(VerticalTilesItemUI, VerticalTilesItemMaterialRenderer);
    registerUIDeserializable(ColumnListUI, ColumnListMaterialRenderer);
    registerUIDeserializable(ColumnItemUI, ColumnItemMaterialRenderer);
    registerUIDeserializable(GalleryListUI, GalleryListMaterialRenderer);
    registerUIDeserializable(GalleryItemUI, GalleryItemMaterialRenderer);
    registerUIDeserializable(CalendarListUI, CalendarListMaterialRenderer);
    registerUIDeserializable(TinderUI, TinderMaterialRenderer);
    registerUIDeserializable(TitleUI, TitleMaterialRenderer);
    registerUIDeserializable(MapUI, MapMaterialRenderer);
    registerUIDeserializable(PieChartUI, PieChartMaterialRenderer);
    registerUIDeserializable(BarChartUI, BarChartMaterialRenderer);
    registerUIDeserializable(StackedBarChartUI, StackedBarChartMaterialRenderer);
    registerUIDeserializable(YouTubeUI, YouTubeMaterialRenderer);
    registerUIDeserializable(CommentsUI, CommentsMaterialRenderer);
    registerUIDeserializable(ChatUI, ChatMaterialRenderer);
    registerUIDeserializable(ButtonUI, ButtonMaterialRenderer);
    registerUIDeserializable(ButtonBarUI, ButtonBarMaterialRenderer);
    registerUIDeserializable(CardItemUI, CardItemMaterialRenderer);
    registerUIDeserializable(TagOverlayUI, TagOverlayMaterialRenderer);
    registerUIDeserializable(AvatarOverlayUI, AvatarOverlayMaterialRenderer);
    registerUIDeserializable(ToggleIconOverlayUI, ToggleIconOverlayMaterialRenderer);
    registerUIDeserializable(InlineListUI, InlineListMaterialRenderer);
    registerUIDeserializable(TextFieldUI, TextFieldMaterialRenderer);
    registerUIDeserializable(DateTimeFieldUI, DateTimeFieldMaterialRenderer);
    registerUIDeserializable(DateFieldUI, DateFieldMaterialRenderer);
    registerUIDeserializable(NumberFieldUI, NumberFieldMaterialRenderer);
    registerUIDeserializable(PhoneFieldUI, PhoneFieldMaterialRenderer);
    registerUIDeserializable(VideoUI, VideoMaterialRenderer);
    registerUIDeserializable(SeparatorUI, SeparatorMaterialRenderer);
    registerUIDeserializable(PivotPickerUI, PivotPickerMaterialRenderer);
    registerUIDeserializable(EmailAuthenticationUI, EmailAuthenticationMaterialRenderer);
    registerUIDeserializable(ImagePickerUI, ImagePickerMaterialRenderer);
    registerUIDeserializable(FilePickerUI, FilePickerMaterialRenderer);
    registerUIDeserializable(LikeUI, LikeMaterialRenderer);
    registerUIDeserializable(ReactionUI, ReactionMaterialRenderer);
    registerUIDeserializable(RatingUI, RatingMaterialRenderer);
    registerUIDeserializable(ProgressUI, ProgressMaterialRenderer);
    registerUIDeserializable(CircleProgressUI, ProgressMaterialRenderer);
    registerUIDeserializable(BuyScreenUI, BuyScreenMaterialRenderer);
    registerUIDeserializable(SimpleTableUI, SimpleTableMaterialRenderer);
    registerUIDeserializable(EmailFieldUI, EmailFieldMaterialRenderer);
    registerUIDeserializable(UserProfileUI, UserProfileMaterialRenderer);
    registerUIDeserializable(WebviewUI, WebviewMaterialRenderer);
    registerUIDeserializable(OverlayEditorUI, OverlayEditorMaterialRenderer);
    registerUIDeserializable(LocationEntryUI, LocationEntryMaterialRenderer);
    registerUIDeserializable(StopwatchUI, StopwatchMaterialRenderer);
    registerUIDeserializable(SignatureFieldUI, SignatureFieldMaterialRenderer);
    registerUIDeserializable(SignaturePadUI, SignaturePadMaterialRenderer);
    registerUIDeserializable(CodeScannerUI, CodeScannerMaterialRenderer);
    registerUIDeserializable(LiveLinearBarcodeScannerUI, LiveLinearBarcodeScannerMaterialRenderer);
    registerUIDeserializable(EventPickerUI, EventPickerMaterialRenderer);
    registerUIDeserializable(EventPickerButtonUI, EventPickerButtonMaterialRenderer);
    registerUIDeserializable(WeatherUI, WeatherMaterialRenderer);
    registerUIDeserializable(LocalTimeUI, LocalTimeMaterialRenderer);

    Object.entries(fluentComponents).forEach(([componentName, component]) => {
        const renderer = makeFluentRenderer(componentName as FluentComponentName);
        if (renderer !== undefined) {
            registerUIDeserializable(makeFluentComponentUI(component, componentName), renderer);
        }
    });
}
